import { useEffect, useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { PayPalScriptProvider, ReactPayPalScriptOptions } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { PageName } from '@/enums/pageName'
import { PaymentPlatform } from '@/enums/payments'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { formatCurrency } from '@/utils/currencyUtils'
import { getContentDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { PageTitle } from '@/components/pageTitle'
import { LTPPayPalButtonV2 } from '@/components/payPalButton'
import { PaymentScreenPolicy } from '@/components/policyLinkBlock'
import { Receipt } from '@/components/receipt'
import { LTPStripeCheckoutFormV2 } from '@/components/stripeCheckoutForm'
import { TextBlock } from '@/components/textBlock'
import { LTPWalletPaymentButtonV2 } from '@/components/walletPaymentButton'

import useTrackLTPPageAndPaymentScreen from './hooks/trackWebEvents/useTrackLTPWebEvents'
import { TPaymentScreenLTPPageProps } from './types'

const paypalOptions: ReactPayPalScriptOptions = {
  clientId: window.config.PAY_PAL_CLIENT_ID,
  components: 'buttons',
  intent: 'capture',
}

const pageName = PageName.ltpPaymentScreen

export const LTPPaymentScreenModal = ({
  getItem,
  setItem,
  pageConfig,
  selectLTPPage,
  config,
  stripePromise,
  stripeFormAppearance,
  paypalButtonConfigStyle,
  getStripeWalletButtonStyles,
  successRedirectTo = PageName.paymentSuccess,
}: TPaymentScreenLTPPageProps) => {
  const {
    discount,
    pageTitle,
    receiptData,
    receiptTitle,
    stripeFormConfig,
    walletButtonConfig,
    paypalCustomTagline,
    disclaimerTemplate,
  } = pageConfig

  const showPayPal = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
    [config]
  )
  const showStripe = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
    [config]
  )

  const selectedContentId = getItem(PageName.selectLTP)
  const contentData = useMemo(
    () => selectLTPPage.contentDetails.find((content) => content.contentId === selectedContentId),
    [selectLTPPage.contentDetails, selectedContentId]
  )

  const navigate = useNavigate()

  useEffect(() => {
    // should be run once
    if (!contentData) {
      return navigate(getPath(PageName.selectLTP))
    }
  }, [navigate, contentData])

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getContentDefaultStripeOptions(contentData, stripeFormAppearance),
    [contentData, stripeFormAppearance]
  )

  const className = `page page-${pageName}`

  useTrackLTPPageAndPaymentScreen(config, getItem, pageTitle, contentData, pageName)

  const disclaimer = useMemo(() => {
    let amount = ''
    if (contentData) {
      amount = formatCurrency(contentData.price, contentData.currency, 2, true)
    }

    return disclaimerTemplate.replace('{price}', amount)
  }, [disclaimerTemplate, contentData])

  if (!contentData || !selectedContentId) {
    return <Navigate to={getPath(PageName.selectLTP)} />
  }

  if (!stripeOptions) {
    return null
  }

  return (
    <div className={className}>
      <PayPalScriptProvider options={{ ...paypalOptions }}>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <PageTitle title={discount} />
          <div className="floating-container ">
            {showStripe && (!walletButtonConfig || !walletButtonConfig.underForm) && (
              <LTPWalletPaymentButtonV2
                contentDetails={contentData}
                getItem={getItem}
                setItem={setItem}
                pageName={pageName}
                config={config}
                stripeButtonTheme={StripeWalletButtonTheme.dark}
                getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                successRedirectTo={successRedirectTo}
              />
            )}

            {receiptData && selectedContentId && (
              <Receipt receiptData={receiptData[selectedContentId]} receiptTitle={receiptTitle} />
            )}

            <div className="buttons-container">
              {showStripe && walletButtonConfig && walletButtonConfig.underForm && (
                <LTPWalletPaymentButtonV2
                  contentDetails={contentData}
                  getItem={getItem}
                  setItem={setItem}
                  pageName={pageName}
                  config={config}
                  stripeButtonTheme={StripeWalletButtonTheme.dark}
                  getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                  showDelimiter={walletButtonConfig.showDelimiter}
                  buttonsDelimiterText={walletButtonConfig.delimiterText}
                  successRedirectTo={successRedirectTo}
                />
              )}
              {showPayPal && (
                <div className="paypal-container">
                  <LTPPayPalButtonV2
                    pageName={pageName}
                    paypalButtonConfigStyle={paypalButtonConfigStyle}
                    contentDetails={contentData}
                    getItem={getItem}
                    setItem={setItem}
                    config={config}
                    customTagline={paypalCustomTagline}
                    successRedirectTo={successRedirectTo}
                  />
                </div>
              )}
            </div>
            {showStripe && (
              <LTPStripeCheckoutFormV2
                pageName={pageName}
                getItem={getItem}
                setItem={setItem}
                contentDetails={contentData}
                config={config}
                formConfig={stripeFormConfig}
                successRedirectTo={successRedirectTo}
              />
            )}

            <PaymentScreenPolicy />
            <TextBlock className="subscriptions-details" text={disclaimer}></TextBlock>
          </div>
        </Elements>
      </PayPalScriptProvider>
    </div>
  )
}
